import { Injectable } from '@angular/core';
import { addDoc, collection, deleteDoc, doc, documentId, endAt, Firestore, getDoc, getDocs, onSnapshot, orderBy, Query, query,
    setDoc, startAt, Unsubscribe, updateDoc, where, writeBatch, getCountFromServer, getAggregateFromServer,
    sum } from '@angular/fire/firestore';
import { ListHelper } from '@classes/list-helper';
import { ObjectHelper } from '@classes/object-helper';
import { Filter } from '@models/filter.model';
import { Sort } from '@models/sort.model';
import { DocumentData, DocumentReference } from 'rxfire/firestore/interfaces';
import { catchError, forkJoin, from, map, NotFoundError, Observable, of, Subject, switchMap, take } from 'rxjs';
import { GUEST_USER_ID } from './user.service';
import { Store } from '@ngrx/store';
import { SessionStoreSelectors, TrailerStoreSelectors } from 'src/app/root-store';
import { Auth } from '@angular/fire/auth';

// Note: take(1) added to all calls cause they were executing again when logging out

const PUBLIC_PATHS = ['gasPrices', 'rates'];
const GUEST_EMPTY_PATHS = ['trailers', 'towRequests', 'tows', 'driverBusyTimes'];
const PUBLIC_SAVE_PATHS = ['towAttempts'];

@Injectable({
    providedIn: 'root'
})
export class DbService {
    constructor(
        private firestore: Firestore,
        private store: Store,
        private auth: Auth
    ) {}

    private isGuest$ = this.store.select(SessionStoreSelectors.getUserId).pipe(
        map(userId => {
            // If the userId is defined, check if it's the guest user id
            if (!!userId) {
                return userId === GUEST_USER_ID;
            }

            // If not defined, check if is a logged in user - this scenario happens during login
            return !this.auth.currentUser;
        })
    );

    getList<T>(path: string, filters?: Filter[], sort?: Sort, startAtValue?: any, endAtValue?: any,
        otherFixes?: Function): Observable<T[]> {
        return this.getPath(path).pipe(switchMap(dbPath => {
            // If the path is 'trailers' and the dbPath is 'empty', we are in the middle of a guest adding a trailer
            // so let's get the guest trailer from the store
            if (path === 'trailers' && dbPath === 'empty') {
                const trailer$ = this.store.select(TrailerStoreSelectors.getTrailer(GUEST_USER_ID)).pipe(take(1));
                return trailer$.pipe(
                    map(trailer => trailer ? [trailer as T] : []),
                    take(1)
                );
            }

            if (dbPath === 'empty') {
                return of([]).pipe(take(1));
            }

            const q = this.list(dbPath, filters, sort, startAtValue, endAtValue);
            const data$ = from(getDocs(q));
            return ListHelper.convertObservableQuerySnapshotToList<T>(data$).pipe(
                map(list => {
                    list.forEach(obj => {
                        this.fixObj(obj, undefined, otherFixes);
                    });
                    return list;
                }),
                catchError(error => {
                    console.error('getList', dbPath, error);
                    throw error;
                }),
                take(1)
            );
        }));
    }

    getListChanges<T>(path: string, sub$: Subject<T[]>, filters?: Filter[], sort?: Sort, startAtValue?: any, endAtValue?: any,
        otherFixes?: Function): Unsubscribe {
        const q = this.list(path, filters, sort, startAtValue, endAtValue);
        const unsubs = onSnapshot(q, x => {
            const list: T[] = [];
            x.docs.forEach(y => {
                const obj = y.data() as T;
                otherFixes?.(obj);
                this.fixObj(obj, y.id);
                list.push(obj);
            });
            sub$?.next(list);
        });
        return unsubs;
    }

    getObj<T>(path: string, id: string): Observable<T> {
        return this.getPath(path).pipe(switchMap(dbPath => {
            // If the path is 'trailers' and the dbPath is 'empty', we are in the middle of a guest adding a trailer
            // so let's get the guest trailer from the store
            if (path === 'trailers' && dbPath === 'empty') {
                const trailer$ = this.store.select(TrailerStoreSelectors.getTrailer(GUEST_USER_ID)).pipe(take(1));
                return trailer$.pipe(
                    map(trailer => (trailer ?? { id }) as T),
                    take(1)
                );
            }

            if (dbPath === 'empty' || (path === 'users' && id === GUEST_USER_ID)) {
                return of({ id } as T).pipe(take(1));
            }

            const docRef = doc(this.firestore, dbPath, id);
            return from(getDoc(docRef)).pipe(
                map(x => {
                    const obj = x.data() as T;
                    if (!obj) {
                        throw NotFoundError;
                    }

                    this.fixObj(obj, id);
                    return obj;
                }),
                catchError(error => {
                    console.error('getObj', dbPath, error);
                    throw error;
                }),
                take(1)
            );
        }));
    }

    getObjChanges<T>(path: string, id: string, sub$: Subject<T>, otherFixes?: Function): Unsubscribe {
        const docRef = doc(this.firestore, path, id);
        const unsub = onSnapshot(docRef, x => {
            const obj = x.data() as T;
            if (!obj) {
                throw NotFoundError;
            }

            this.fixObj(obj, id, otherFixes);
            sub$.next(obj);
        });
        return unsub;
    }

    saveObj<T>(path: string, obj: T, type: 'add' | 'set' = 'add'): Observable<T> {
        obj = ObjectHelper.cloneObject(obj);
        let data = obj as any;
        const id = data.id;
        delete data.id;
        data = ObjectHelper.removeEmptyProperties(data);

        return this.isGuest$.pipe(
            take(1),
            switchMap(isGuest => {
                if (isGuest && !PUBLIC_SAVE_PATHS.includes(path)) {
                    return of({ ...data, id } as T).pipe(take(1));
                }

                const collectionRef = collection(this.firestore, path);
                let docRef!: DocumentReference<DocumentData>;
                if (id) {
                    docRef = doc(this.firestore, path, id);
                }

                switch (type) {
                    case 'set':
                        return from(getDoc(docRef)).pipe(
                            switchMap(ds => {
                                if (ds.exists()) {
                                    return this.update<T>(data, id, docRef);
                                }

                                data.creationDate = new Date();
                                return from(setDoc(doc(this.firestore, path, id), data)).pipe(
                                    map(() => {
                                        return { ...data, id } as T;
                                    })
                                );
                            }),
                            catchError(error => {
                                console.error('saveObj', path, error);
                                throw error;
                            }),
                            take(1)
                        );
                    case 'add':
                    default:
                        if (id) {
                            return this.update<T>(data, id, docRef);
                        }

                        data.creationDate = new Date();
                        return from(addDoc(collectionRef, data)).pipe(
                            map(x => {
                                return { ...data, id: x.id } as T;
                            }),
                            catchError(error => {
                                console.error('saveObj', path, error);
                                throw error;
                            }),
                            take(1)
                        );
                }
            })
        );
    }

    saveList<T>(path: string, list: T[], type: 'add' | 'set' = 'add'): Observable<T[]> {
        const saveObjList$ = list.map(obj => this.saveObj(path, obj, type));
        return forkJoin(saveObjList$);
    }

    deleteObj(path: string, id: string): Observable<string> {
        const docRef = doc(this.firestore, path, id);
        return from(deleteDoc(docRef)).pipe(
            map(() => id),
            catchError(error => {
                console.error('deleteObj', path, error);
                throw error;
            }),
            take(1)
        );
    }

    updateList<T>(path: string, list: T[]): Observable<T[]> {
        const batch = writeBatch(this.firestore);

        list.forEach(obj => {
            const docRef = doc(this.firestore, path, (obj as any).id);
            batch.update(docRef, obj as any);
        });

        return from(batch.commit()).pipe(
            map(() => list)
        );
    }

    updateObjProperties<T>(path: string, id: string, data: T): Observable<T> {
        const docRef = doc(this.firestore, path, id);
        return this.update<T>(data, id, docRef);
    }

    getCount(path: string, filters?: Filter[]): Observable<number> {
        const q = this.list(path, filters);
        const snapshot$ = from(getCountFromServer(q));
        return snapshot$.pipe(map(snapshot => snapshot.data().count));
    }

    getSum(path: string, field: string, filters?: Filter[]): Observable<number> {
        const q = this.list(path, filters);
        const snapshot$ = from(getAggregateFromServer(q, {
            sum: sum(field)
        }));
        return snapshot$.pipe(map(snapshot => snapshot.data().sum));
    }

    private list(path: string, filters?: Filter[], sort?: Sort, startAtValue?: any, endAtValue?: any): Query<DocumentData> {
        const collectionRef = collection(this.firestore, path);
        let q = query(collectionRef);

        filters?.forEach(filter => {
            if (filter.field === 'id') {
                q = query(q, where(documentId(), filter.operator, filter.value));
                return;
            }

            q = query(q, where(filter.field, filter.operator, filter.value));
        });

        if (sort) {
            q = query(q, orderBy(sort.field, sort.direction));
        }

        if (startAtValue) {
            q = query(q, startAt(startAtValue));
        }

        if (endAtValue) {
            q = query(q, endAt(endAtValue));
        }

        return q;
    }

    private fixObj<T>(obj: T, id?: string, otherFixes?: Function): void {
        if (id) {
            (obj as any).id = id;
        }

        ObjectHelper.fixDate(obj, 'creationDate');
        otherFixes?.(obj);
    }

    private update<T>(data: T, id: string, docRef: DocumentReference<DocumentData>): Observable<T> {
        return this.isGuest$.pipe(
            take(1),
            switchMap(isGuest => {
                // Don't execute if is a guest user
                if (isGuest) {
                    return of({ ...data, id } as T).pipe(take(1));
                }

                return from(updateDoc(docRef, data as any)).pipe(
                    map(() => {
                        return { ...data, id } as T;
                    }),
                    catchError(error => {
                        console.error('update', error, data);
                        throw error;
                    }),
                    take(1)
                );
            })
        );
    }

    private getPath(path: string): Observable<string> {
        // If this is a public path, just return the path
        if (PUBLIC_PATHS.includes(path)) {
            return of(path);
        }

        return this.isGuest$.pipe(
            take(1),
            map(isGuest => {
                const isEmptyPath = GUEST_EMPTY_PATHS.includes(path);
                if (isGuest && isEmptyPath) {
                    return 'empty';
                }

                // If this is a guest user, add 'Public' to the path. Otherwise, just return the path
                return isGuest ? `${path}Public` : path;
            })
        );
    }
}
